import { useContext, type FC } from "react";
import { useTranslation } from "react-i18next";
import CampaignFlowActions from "../../../shared/CampaignFlowActions";
import { CampaignStepCardChromeContext } from "../../../shared/CampaignStepCard";
import TemplateSubmittedModal from "../components/TemplateSubmittedModal";
import { useWhatsAppTemplateForm } from "../hooks/UseWhatsAppTemplateForm";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppTemplate } from "../WhatsAppCampaignTypes";
import WhatsAppTemplateFormStep from "./WhatsAppTemplateFormStep";

interface WhatsAppTemplateFormScreenProps {
  template: WhatsAppTemplate | null;
  sendTo: string[];
  useAi: boolean;
  onBack: () => void;
  onFinished: () => void;
  embedded?: boolean;
}

const WhatsAppTemplateFormScreen: FC<WhatsAppTemplateFormScreenProps> = ({
  template,
  sendTo,
  useAi,
  onBack,
  onFinished,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const chrome = useContext(CampaignStepCardChromeContext);
  const form = useWhatsAppTemplateForm({
    initialTemplate: template,
    initialSendTo: sendTo,
    useAi,
    onDraftSaved: onFinished,
  });
  const { isSaving, isReadOnly, submittedName, media } = form;
  const isBusy = isSaving || media.isUploading;

  const handleDone = () => {
    form.closeSubmitted();
    onFinished();
  };

  const actions = (
    <CampaignFlowActions sticky={!embedded} wide onBack={onBack} backDisabled={isSaving}>
      {!isReadOnly && (
        <>
          <button
            type="button"
            className="button button--link campaign-flow__action campaign-flow__action--link"
            onClick={form.saveDraft}
            disabled={isBusy}
          >
            {t("campaignFlow.common.saveDraft")}
          </button>
          <button
            type="button"
            className="button button--primary campaign-flow__action"
            onClick={form.submit}
            disabled={isBusy}
          >
            {t(`${WHATSAPP_EDITOR_KEY}.form.submit`)}
          </button>
        </>
      )}
    </CampaignFlowActions>
  );

  return (
    <>
      {embedded ? (
        <CampaignStepCardChromeContext.Provider value={{ ...chrome, footer: actions }}>
          <WhatsAppTemplateFormStep form={form} aiPromptInCard />
        </CampaignStepCardChromeContext.Provider>
      ) : (
        <>
          <WhatsAppTemplateFormStep form={form} />
          {actions}
        </>
      )}
      {submittedName && <TemplateSubmittedModal templateName={submittedName} onDone={handleDone} />}
    </>
  );
};

export default WhatsAppTemplateFormScreen;
